import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react'

export function DeleteAccountCard({ userId, email, fullName }: { userId: string | null; email: string | null; fullName?: string | null }) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)

  const { data: request } = useQuery({
    queryKey: ['my-deletion-request', userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('account_deletion_requests' as any)
        .select('id, status, created_at')
        .eq('user_id', userId!)
        .eq('status', 'pending')
        .maybeSingle() as any)
      return data as { id: string; status: string; created_at: string } | null
    },
  })

  const submit = async () => {
    if (!userId) return
    setBusy(true)
    try {
      const { error } = await (supabase
        .from('account_deletion_requests' as any)
        .insert({ user_id: userId, email, full_name: fullName ?? null, reason: reason.trim() || null }) as any)
      if (error) throw error
      toast({ title: 'Deletion request submitted', description: 'Our team will permanently remove your account.' })
      setOpen(false); setConfirmText(''); setReason('')
      qc.invalidateQueries({ queryKey: ['my-deletion-request', userId] })
    } catch (err: any) {
      toast({ title: 'Could not submit request', description: err.message, variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  const cancelRequest = async () => {
    if (!request) return
    setBusy(true)
    try {
      const { error } = await (supabase
        .from('account_deletion_requests' as any)
        .update({ status: 'cancelled' })
        .eq('id', request.id) as any)
      if (error) throw error
      toast({ title: 'Deletion request cancelled' })
      qc.invalidateQueries({ queryKey: ['my-deletion-request', userId] })
    } catch (err: any) {
      toast({ title: 'Could not cancel', description: err.message, variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-destructive">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {request ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
            <p className="text-sm font-medium text-destructive">Account deletion requested</p>
            <p className="text-xs text-muted-foreground text-justify">
              Submitted on {new Date(request.created_at).toLocaleDateString()}. Your account and profile will be permanently deleted once reviewed. You can cancel while the request is still pending.
            </p>
            <Button variant="outline" size="sm" disabled={busy} onClick={cancelRequest}>
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Cancel request
            </Button>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground text-justify">
              Deleting your account permanently removes your journalist profile, documents, accreditation records and submission history. Accreditations already issued will be revoked. This cannot be undone.
            </p>
            <Button variant="destructive" size="sm" className="gap-1.5" onClick={() => setOpen(true)} disabled={!userId}>
              <Trash2 className="w-3.5 h-3.5" /> Delete my account
            </Button>
          </>
        )}
      </CardContent>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete your account?</AlertDialogTitle>
            <AlertDialogDescription className="text-justify">
              This removes your profile, uploaded documents, accreditations and coverage history. This action cannot be reversed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Reason (optional)</Label>
              <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Tell us why you are leaving…" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Type DELETE to confirm</Label>
              <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="DELETE" />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep my account</AlertDialogCancel>
            <AlertDialogAction
              disabled={confirmText !== 'DELETE' || busy}
              onClick={(e) => { e.preventDefault(); submit() }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
              Delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
